import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import DownloadPDFButton from "./DownloadPdfButton";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const QuestionItem = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    "&:last-child": { borderBottom: "none" },
    "&:hover": { backgroundColor: "rgba(255,255,255,0.02)" },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ MAIN COMPONENT */
export default function JavaAssignment() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleCopy = (questions, index) => {
        const text = questions.join("\n");
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const assignmentData = useMemo(() => [
        {
            title: "Data Types & Control Structures",
            questions: [
                "Check if a number is prime (Input validation included).",
                "Determine if a character is vowel or consonant using switch.",
                "Generate Fibonacci sequence up to N terms.",
                "Find the largest of three numbers using ternary operators.",
                "Build a simple calculator using switch-case."
            ]
        },
        {
            title: "Methods & Scope",
            questions: [
                "Recursive function to return factorial of a number.",
                "Check if a string is palindrome (Ignore case/spaces).",
                "Demonstrate method overloading (area of circle vs rectangle).",
                "Show side-effects of local vs global variables in a class.",
                "Count total vowels and consonants in a given string."
            ]
        },
        {
            title: "Object‑Oriented Programming (OOP)",
            questions: [
                "Construct a 'Car' class with specific attributes & methods.",
                "Simulate a 'School System' using multi-level inheritance.",
                "Implement method overriding for a 'Shape' hierarchy.",
                "Encapsulate 'UserAccount' using private fields & validation.",
                "Build a simple ATM Banking system with transaction logic."
            ]
        },
        {
            title: "Arrays & Strings",
            questions: [
                "Algorithm to find the second largest number in an array.",
                "Reverse a string without using built-in .reverse().",
                "Implement a basic Bubble Sort for integer arrays.",
                "Find the frequency of each character in a given String.",
                "Merge two arrays into a single, sorted result array."
            ]
        },
        {
            title: "Exception Handling",
            questions: [
                "Gracefully handle division by zero errors.",
                "Define and trigger a Custom 'InvalidAgeException'.",
                "Demonstrate multiple catch blocks with specific vs generic types.",
                "Use the 'finally' block for mandatory resource cleanup.",
                "Validate user registration data and throw exceptions on failure."
            ]
        },
        {
            title: "Collections & Multithreading",
            questions: [
                "Filter and Sort a List of Employees using ArrayList.",
                "Eliminate duplicate user IDs from a list using HashSet.",
                "Map student names to their grades using HashMap.",
                "Synchronize a shared resource between two competing threads.",
                "Use ExecutorService to process a batch of 5 background tasks."
            ]
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    JAVA MASTER ASSIGNMENT
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 🎯 Assignment Strategy & Overview */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Curriculum Coverage
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
                            This master assignment is designed to transform theory into professional-grade logic. Each module contains <strong>5 high-frequency interview questions</strong>. To successfully complete this course, you must implement these in your local environment.
                        </Typography>



                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            {[
                                "Industry-standard coding challenges",
                                "Focus on Edge-case handling",
                                "OOP & Design Pattern practice",
                                "Clean Code & Documentation habits",
                                "Prepares for Technical Interviews"
                            ].map((item, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <CheckIcon sx={{ color: "goldenrod", fontSize: 20 }} />
                                        <Typography variant="body2">{item}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* ✅ Assignment Modules */}
                {assignmentData.map((topic, index) => (
                    <Grid item xs={12} key={index}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiFillBulb size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {topic.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === index ? "Questions Copied!" : "Copy Task List"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(topic.questions, index)}
                                            sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}
                                        >
                                            {copiedIndex === index ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{ color: "goldenrod", borderColor: "goldenrod" }}
                                        >
                                            Code Now
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                {topic.questions.map((q, i) => (
                                    <QuestionItem key={i}>
                                        <Typography sx={{ color: "goldenrod", fontWeight: "bold" }}>{i + 1}.</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>{q}</Typography>
                                    </QuestionItem>
                                ))}
                            </Box>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Final Actions Footer */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Stack direction={isMobile ? "column" : "row"} spacing={2} justifyContent="center">
                        <Link to="/javadashboard" style={{ textDecoration: "none" }}>
                            <ActionButton variant="contained">
                                BACK TO DASHBOARD
                            </ActionButton>
                        </Link>
                        <DownloadPDFButton />
                    </Stack>
                </Grid>
            </Grid>

            {/* ✅ Floating Scroll-to-Top */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
